import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Receipt, DollarSign, CheckCircle2, Clock, PlusCircle } from "lucide-react";
import { api } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, StatCard, EmptyState, StatusBadge, money, shortDate } from "../components/common";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

export default function RetailerDashboard() {
  const { data: receipts = [], isLoading } = useQuery({
    queryKey: ["issued"],
    queryFn: async () => (await api.get("/receipts/issued")).data,
  });

  const total = receipts.reduce((s, r) => s + (r.total || 0), 0);
  const claimed = receipts.filter((r) => r.status !== "issued").length;
  const pending = receipts.length - claimed;

  return (
    <AppLayout>
      <PageHeader
        eyebrow="Retailer"
        title="Dashboard"
        subtitle="Issue digital receipts and track how many customers have claimed them."
        actions={
          <Link to="/retailer/issue">
            <Button data-testid="issue-receipt-cta" className="bg-brand text-white hover:bg-brand-hover">
              <PlusCircle className="mr-2 h-4 w-4" /> Issue receipt
            </Button>
          </Link>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total receipts" value={receipts.length} icon={Receipt} />
        <StatCard label="Total value" value={money(total)} icon={DollarSign} accent="bg-success/12 text-success" />
        <StatCard label="Claimed" value={claimed} icon={CheckCircle2} accent="bg-brand-muted text-brand" />
        <StatCard label="Awaiting claim" value={pending} icon={Clock} accent="bg-warning/12 text-warning" />
      </div>

      <div className="mt-8 rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="font-heading text-lg font-bold">Recent receipts</h2>
          <Link to="/retailer/receipts" className="text-sm font-semibold text-brand hover:underline">View all</Link>
        </div>
        {isLoading ? (
          <div className="space-y-3 p-5">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : receipts.length === 0 ? (
          <EmptyState icon={Receipt} title="No receipts yet" subtitle="Issue your first digital receipt to see it here."
            action={<Link to="/retailer/issue"><Button className="bg-brand text-white hover:bg-brand-hover">Issue receipt</Button></Link>} />
        ) : (
          <div className="divide-y divide-border">
            {receipts.slice(0, 8).map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-4 px-5 py-3.5" data-testid={`receipt-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-semibold">{r.items?.[0]?.name || "Purchase"}</p>
                  <p className="text-xs text-muted-foreground">Code {r.claim_code} · {shortDate(r.created_at)}</p>
                </div>
                <div className="flex items-center gap-4">
                  <StatusBadge status={r.status} />
                  <span className="font-mono text-sm font-bold tabular-nums">{money(r.total)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
